import type { ReactNode } from 'react'
import { useState } from 'react'
import {
  Box,
  Button,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { DashboardType } from '../types'
import { DashboardsTable } from './DashboardsTable'

/** Sentinel for the unfiltered state — MUI Select cannot hold undefined as a value. */
const ALL_TYPES = 'ALL'

const TYPE_OPTIONS = [
  DashboardType.HOMEPAGE,
  DashboardType.SUMMARY,
  DashboardType.INSIGHTS,
] as const

export function DashboardsPage({ children }: { children?: ReactNode }) {
  const { t } = useTranslation('admin-console')
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [type, setType] = useState<DashboardType | typeof ALL_TYPES>(ALL_TYPES)

  return (
    <Stack
      spacing={2}
      sx={{
        px: 2,
        pt: 2,
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
        }}
      >
        {t('dashboards.page.title')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            variant="outlined"
            placeholder={t('dashboards.page.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ '& .MuiOutlinedInput-root': { height: 32 } }}
          />
          <Select
            size="small"
            value={type}
            onChange={(e) => setType(e.target.value)}
            inputProps={{ 'aria-label': t('dashboards.page.selectType') }}
            sx={{ height: 32, minWidth: 160 }}
          >
            <MenuItem value={ALL_TYPES}>{t('dashboards.page.selectType')}</MenuItem>
            {TYPE_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {t(`dashboards.table.type.${option}`)}
              </MenuItem>
            ))}
          </Select>
        </Stack>
        <Button
          variant="contained"
          size="small"
          onClick={() => void navigate({ to: '/preferences/dashboards/new' })}
        >
          {t('dashboards.page.newDashboard')}
        </Button>
      </Box>
      <DashboardsTable search={search} type={type === ALL_TYPES ? undefined : type} />
      {children}
    </Stack>
  )
}
